import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Users, UserPlus, Mail, Loader2, Trash2, ChevronLeft, ShieldCheck,
  Wrench, Headphones, Gauge, Crown, X, CheckCircle2, Send, Copy, KeyRound,
} from "lucide-react";

const ROLE_LABELS = {
  owner:      { label: "Owner",          icon: Crown,        cls: "bg-warning/15 text-warning border-warning/30",        desc: "Full control of the workshop" },
  manager:    { label: "Manager",        icon: ShieldCheck,  cls: "bg-primary/15 text-primary border-primary/30",        desc: "Can invite + change roles" },
  advisor:    { label: "Service Advisor",icon: Headphones,   cls: "bg-info/15 text-info border-info/30",                 desc: "Customer-facing flows + quotes" },
  controller: { label: "Controller",     icon: Gauge,        cls: "bg-info/15 text-info border-info/30",                 desc: "Workshop loading + DMS" },
  technician: { label: "Technician",     icon: Wrench,       cls: "bg-muted text-foreground border-border",              desc: "Records videos + completes QC" },
};

const ASSIGNABLE = ["manager", "advisor", "controller", "technician"];

export default function TeamPage() {
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);

  const reload = async () => {
    try {
      const [meR, mR, iR] = await Promise.all([
        api.get("/auth/me"),
        api.get("/team"),
        api.get("/team/invites").catch(() => ({ data: { invites: [] } })),
      ]);
      setMe(meR.data);
      setMembers(mR.data.members || []);
      setInvites(iR.data.invites || []);
    } catch {
      toast.error("Couldn't load team");
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const isOwner = me?.role_in_workshop === "owner";
  const canInvite = isOwner || me?.role_in_workshop === "manager";

  const onRoleChange = async (member, role) => {
    try {
      await api.patch(`/team/${member.id}/role`, { role });
      toast.success(`${member.name || member.email} → ${ROLE_LABELS[role].label}`);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't change role"); }
  };
  const onRemove = async (member) => {
    if (!window.confirm(`Remove ${member.name || member.email} from the team? They'll lose access immediately.`)) return;
    try {
      await api.delete(`/team/${member.id}`);
      toast.success("Removed from team");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't remove"); }
  };
  const onSendResetLink = async (member) => {
    if (!window.confirm(`Send a password-reset link to ${member.name || member.email}? They'll have 30 minutes to use it.`)) return;
    try {
      const r = await api.post("/auth/admin-reset", { user_id: member.id });
      toast.success(r.data?.message || `Reset link sent to ${member.email}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send reset link");
    }
  };
  const onCancelInvite = async (inv) => {
    try {
      await api.delete(`/team/invites/${inv.id}`);
      toast.success("Invite cancelled");
      reload();
    } catch { toast.error("Couldn't cancel"); }
  };
  const onResendInvite = async (inv) => {
    try {
      const r = await api.post(`/team/invites/${inv.id}/resend`);
      toast.success(r.data.email_sent ? "Invite resent" : "Sent (email channel offline — share the link manually)");
    } catch { toast.error("Couldn't resend"); }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="team-page">
      <header className="border-b border-border sticky top-0 bg-background/80 backdrop-blur z-30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="team-back-link">
            <ChevronLeft className="w-4 h-4" /> Dashboard
          </Link>
          <div className="flex items-center gap-2 text-sm font-bold">
            <Users className="w-4 h-4 text-primary" />
            My team
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 md:py-10 space-y-6">
        <section>
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <div className="label-overline">Your workshop</div>
              <h1 className="font-display font-black text-2xl md:text-3xl mt-1">
                {me?.workshop_name || "Workshop"}
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                {members.length} member{members.length === 1 ? "" : "s"}{invites.length ? ` · ${invites.length} pending invite${invites.length === 1 ? "" : "s"}` : ""}
              </p>
            </div>
            {canInvite && (
              <button
                onClick={() => setShowInvite(true)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-primary-foreground font-bold text-sm hover:scale-[1.02] transition-transform"
                data-testid="team-invite-btn"
              >
                <UserPlus className="w-4 h-4" /> Invite teammate
              </button>
            )}
          </div>
        </section>

        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        )}

        {/* Pending invites */}
        {!loading && invites.length > 0 && (
          <section className="card-default p-5" data-testid="team-invites-section">
            <h2 className="font-display font-bold text-lg">Pending invites</h2>
            <ul className="mt-3 divide-y divide-border">
              {invites.map((inv) => (
                <li key={inv.id} className="py-3 flex flex-wrap items-center gap-3 justify-between" data-testid={`team-invite-${inv.id}`}>
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{inv.email}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Invited as <strong className="text-foreground">{ROLE_LABELS[inv.role]?.label || inv.role}</strong>
                      {" · expires "}{new Date(inv.expires_at).toLocaleDateString("en-GB")}
                    </div>
                  </div>
                  {canInvite && (
                    <div className="flex items-center gap-2">
                      <button onClick={() => onResendInvite(inv)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-border hover:bg-muted text-xs font-semibold" data-testid={`team-invite-resend-${inv.id}`}>
                        <Send className="w-3 h-3" /> Resend
                      </button>
                      <button onClick={() => onCancelInvite(inv)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-destructive/30 hover:bg-destructive/10 text-destructive text-xs font-semibold" data-testid={`team-invite-cancel-${inv.id}`}>
                        <X className="w-3 h-3" /> Cancel
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Members table */}
        {!loading && (
          <section className="card-default p-5" data-testid="team-members-section">
            <h2 className="font-display font-bold text-lg">Team</h2>
            <ul className="mt-3 divide-y divide-border">
              {members.map((m) => {
                const role = m.role_in_workshop || "advisor";
                const isThem = m.id === me?.id;
                const isTargetOwner = role === "owner";
                const editable = canInvite && !isTargetOwner && !isThem;
                const Roleicon = ROLE_LABELS[role]?.icon || Wrench;
                return (
                  <li key={m.id} className="py-3 flex flex-wrap items-center gap-3 justify-between" data-testid={`team-member-${m.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold truncate">{m.name || "Unnamed"}</span>
                        {isThem && (
                          <span className="text-[10px] uppercase tracking-wider font-bold text-primary">YOU</span>
                        )}
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${ROLE_LABELS[role]?.cls || ""}`} data-testid={`team-member-${m.id}-role`}>
                          <Roleicon className="w-3 h-3" /> {ROLE_LABELS[role]?.label || role}
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2">
                        <Mail className="w-3 h-3" /> {m.email}
                        {m.last_login_at && (
                          <span>· Last sign-in {new Date(m.last_login_at).toLocaleDateString("en-GB")}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {editable && (
                        <select
                          value={role}
                          onChange={(e) => onRoleChange(m, e.target.value)}
                          className="text-xs rounded-full border border-border bg-background px-3 py-1.5 font-semibold"
                          data-testid={`team-member-${m.id}-role-select`}
                        >
                          {ASSIGNABLE.map((r) => (
                            <option key={r} value={r}>{ROLE_LABELS[r].label}</option>
                          ))}
                        </select>
                      )}
                      {isOwner && !isThem && (
                        <button
                          onClick={() => onSendResetLink(m)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-border hover:bg-muted text-xs font-semibold"
                          data-testid={`team-member-${m.id}-send-reset`}
                          title="Email a password-reset link to this user (valid 30 min)"
                        >
                          <KeyRound className="w-3 h-3" /> Reset password
                        </button>
                      )}
                      {isOwner && !isTargetOwner && !isThem && (
                        <button
                          onClick={() => onRemove(m)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-destructive/30 hover:bg-destructive/10 text-destructive text-xs font-semibold"
                          data-testid={`team-member-${m.id}-remove`}
                          title="Remove from team"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        <section className="rounded-2xl border border-primary/30 bg-primary/5 p-5 text-sm">
          <div className="flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold">Workshop-scoped access</p>
              <p className="text-muted-foreground text-xs mt-1">
                Every member only sees data from <strong className="text-foreground">{me?.workshop_name || "your workshop"}</strong>.
                Two dealerships on ServiceSell PRO can never see each other's customers, quotes or videos — GDPR by design.
              </p>
            </div>
          </div>
        </section>
      </main>

      {showInvite && (
        <InviteModal
          onClose={() => setShowInvite(false)}
          onSent={() => { setShowInvite(false); reload(); }}
        />
      )}
    </div>
  );
}


function InviteModal({ onClose, onSent }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("advisor");
  const [busy, setBusy] = useState(false);
  const [acceptUrl, setAcceptUrl] = useState(null);

  const submit = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      const r = await api.post("/team/invite", { email: email.trim().toLowerCase(), role, name: name.trim() || null });
      if (r.data.email_sent) {
        toast.success(`Invite emailed to ${email}`);
        onSent?.();
      } else {
        // Surface the accept URL so the user can hand it over manually
        setAcceptUrl(r.data.accept_url);
        toast.message("Invite created but email channel is offline — copy the link below");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't create invite");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="team-invite-modal">
      <div className="bg-card border border-border rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="font-display font-black text-xl">Invite a teammate</h2>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-muted" data-testid="team-invite-modal-close" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>
        {acceptUrl ? (
          <div className="mt-5 space-y-3">
            <div className="text-sm text-muted-foreground">
              SMTP isn't configured yet, so copy this link and send it to <strong className="text-foreground">{email}</strong> yourself:
            </div>
            <div className="rounded-lg border border-border bg-muted/40 p-3 text-xs break-all font-mono" data-testid="team-invite-accept-url">
              {acceptUrl}
            </div>
            <div className="flex gap-2">
              <button onClick={() => { navigator.clipboard.writeText(acceptUrl); toast.success("Link copied"); }}
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-bold" data-testid="team-invite-copy-link">
                <Copy className="w-4 h-4" /> Copy link
              </button>
              <button onClick={onSent} className="px-4 py-2 rounded-full border border-border text-sm font-semibold">Done</button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-5 space-y-4">
            <label className="block text-sm">
              <span className="font-semibold">Email address</span>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="teammate@example.com"
                className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                data-testid="team-invite-email-input"
              />
            </label>
            <label className="block text-sm">
              <span className="font-semibold">Name <span className="text-muted-foreground font-normal">(optional)</span></span>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Tom Mechanic"
                className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                data-testid="team-invite-name-input"
              />
            </label>
            <label className="block text-sm">
              <span className="font-semibold">Role</span>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                data-testid="team-invite-role-select"
              >
                {ASSIGNABLE.map((r) => (
                  <option key={r} value={r}>{ROLE_LABELS[r].label} — {ROLE_LABELS[r].desc}</option>
                ))}
              </select>
            </label>

            <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
              <CheckCircle2 className="w-3 h-3 text-success flex-shrink-0" />
              We'll email them an accept-link that's valid for 7 days.
            </div>

            <button
              type="submit"
              disabled={busy || !email}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-primary text-primary-foreground font-bold text-sm disabled:opacity-50"
              data-testid="team-invite-submit"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
              Send invite
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
